/**
 * Browser tool title extraction (moved out of session-logic.ts for the
 * timeline port, ticket 3525; behavior unchanged).
 *
 * @module workLogBrowserTitle
 */

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function extractToolTitle(payload: Record<string, unknown> | null): string | null {
  return extractBrowserToolTitle(payload) ?? asTrimmedString(payload?.title);
}

// fallow-ignore-next-line complexity -- port of forge session-logic (pre-port) at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function extractBrowserToolTitle(payload: Record<string, unknown> | null): string | null {
  if (!payload) {
    return null;
  }
  const data = asRecord(payload.data);
  const item = asRecord(data?.item);
  const input = asRecord(item?.input) ?? asRecord(data?.input);
  const title = asTrimmedString(payload.title);
  const itemType = asTrimmedString(payload.itemType);
  const toolName =
    asTrimmedString(item?.name) ??
    asTrimmedString(item?.toolName) ??
    asTrimmedString(data?.name) ??
    asTrimmedString(payload.toolName) ??
    title;
  const detail = asTrimmedString(payload.detail);
  const haystack = [toolName, title, itemType, detail].filter(Boolean).join(" ").toLowerCase();

  if (!haystack.includes("browser") && !haystack.includes("forge-browser")) {
    return null;
  }

  const action = resolveBrowserToolAction(toolName, input, detail);
  switch (action) {
    case "open-session":
      return "Opened browser session";
    case "name-session":
      return "Named browser session";
    case "new-tab":
      return hostLabelFromInput(input) ?? "Opened browser tab";
    case "navigate":
      return hostLabelFromInput(input) ?? "Navigated browser";
    case "reload":
      return "Reloaded page";
    case "back":
      return "Went back";
    case "forward":
      return "Went forward";
    case "screenshot":
      return "Captured screenshot";
    case "snapshot":
      return "Read DOM snapshot";
    case "logs":
      return "Read console logs";
    case "click":
      return "Clicked page";
    case "type":
      return "Typed text";
    case "scroll":
      return "Scrolled page";
    case "viewport":
      return "Updated viewport";
    case "visibility":
      return "Updated browser visibility";
    default:
      return null;
  }
}

type BrowserToolAction =
  | "open-session"
  | "name-session"
  | "new-tab"
  | "navigate"
  | "reload"
  | "back"
  | "forward"
  | "screenshot"
  | "snapshot"
  | "logs"
  | "click"
  | "type"
  | "scroll"
  | "viewport"
  | "visibility";

// fallow-ignore-next-line complexity -- port of forge session-logic (pre-port) at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function resolveBrowserToolAction(
  toolName: string | null,
  input: Record<string, unknown> | null,
  detail: string | null,
): BrowserToolAction | null {
  const commandType = asTrimmedString(input?.type) ?? asTrimmedString(input?.command);
  const raw = [commandType, toolName, detail].filter(Boolean).join(" ").toLowerCase();
  if (/\b(open[-_.\s]?session|create[-_.\s]?session|session[-_.\s]?open)\b/.test(raw)) {
    return "open-session";
  }
  if (/\b(name[-_.\s]?session|session[-_.\s]?name)\b/.test(raw)) {
    return "name-session";
  }
  if (/\b(new[-_.\s]?tab|create[-_.\s]?tab|open[-_.\s]?tab)\b/.test(raw)) {
    return "new-tab";
  }
  if (/\b(navigate|goto|go[-_.\s]?to)\b/.test(raw)) {
    return "navigate";
  }
  if (/\breload\b/.test(raw)) {
    return "reload";
  }
  if (/\b(go[-_.\s]?back|back)\b/.test(raw)) {
    return "back";
  }
  if (/\b(go[-_.\s]?forward|forward)\b/.test(raw)) {
    return "forward";
  }
  if (/\b(screenshot|capture)\b/.test(raw)) {
    return "screenshot";
  }
  if (/\b(snapshot|dom[-_.\s]?snapshot|read[-_.\s]?dom)\b/.test(raw)) {
    return "snapshot";
  }
  if (/\b(console[-_.\s]?logs?|logs?)\b/.test(raw)) {
    return "logs";
  }
  if (/\bclick\b/.test(raw)) {
    return "click";
  }
  if (/\btype|fill\b/.test(raw)) {
    return "type";
  }
  if (/\bscroll\b/.test(raw)) {
    return "scroll";
  }
  if (/\bviewport\b/.test(raw)) {
    return "viewport";
  }
  if (/\bvisibility|visible|hidden\b/.test(raw)) {
    return "visibility";
  }
  return null;
}

function hostLabelFromInput(input: Record<string, unknown> | null): string | null {
  const url = asTrimmedString(input?.url) ?? asTrimmedString(input?.initialUrl);
  if (!url) {
    return null;
  }
  try {
    const parsed = new URL(url);
    if (parsed.hostname.length === 0) {
      return null;
    }
    return `Navigated to ${parsed.hostname}`;
  } catch {
    return null;
  }
}
